/**
 * Smooth in-page anchor navigation that accounts for deferred/async content.
 *
 * On the PDP the star rating below the product title links to the customer
 * reviews section via `href="#reviews"`. That section sits below several
 * lazy-loaded `<lazy-content-section>` blocks and renders an async Yotpo
 * widget. A native hash jump lands on the target's *current* position, which is
 * wrong because the sections above have not expanded yet (and the Yotpo widget
 * has zero height until it loads). Once content settles the target has moved,
 * so the user is left far from the reviews — the reported bug.
 *
 * We intercept clicks on hash links whose target depends on deferred content,
 * force any pending lazy sections to load, then scroll once the layout settles,
 * re-aligning while async widgets finish rendering. Deep links (URL already
 * containing `#reviews`) are handled the same way on load.
 */

/** @constant {number} How long to keep re-aligning after the initial scroll. */
const SETTLE_DURATION_MS = 4000;

/** @constant {number} Delay between re-alignment checks. */
const REALIGN_INTERVAL_MS = 150;

/** @constant {number} Minimum target shift (px) that triggers a re-align. */
const REALIGN_THRESHOLD_PX = 2;

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * @param {Element} target
 * @returns {number}
 */
function getScrollMarginTop(target) {
  const value = parseFloat(getComputedStyle(target).scrollMarginTop);
  return Number.isFinite(value) ? value : 0;
}

/**
 * Absolute document Y position to scroll the target to (accounts for scroll-margin).
 * @param {Element} target
 * @returns {number}
 */
function targetTop(target) {
  return window.scrollY + target.getBoundingClientRect().top - getScrollMarginTop(target);
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
}

/**
 * Force every not-yet-loaded `<lazy-content-section>` to fetch its content now so
 * the target's final position can be computed.
 * @returns {Promise<void>}
 */
async function forcePendingLazySections() {
  const sections = /** @type {Array<HTMLElement & { loadNow?: () => Promise<void> }>} */ (
    Array.from(document.querySelectorAll('lazy-content-section'))
  );

  const pending = sections.filter(
    (el) => typeof el.loadNow === 'function' && !el.hasAttribute('data-loaded')
  );

  if (!pending.length) return;

  await Promise.allSettled(pending.map((el) => el.loadNow?.()));
}

/**
 * Whether reaching this target requires waiting on deferred/async content. Keeps
 * us from hijacking simple in-page anchors on pages with no lazy content.
 * @param {Element} target
 * @returns {boolean}
 */
function targetNeedsSettle(target) {
  if (document.querySelector('lazy-content-section:not([data-loaded])')) return true;

  // Reviews jump: the #reviews target is often an empty anchor block sitting next
  // to an async Yotpo widget (Shopify app block) whose late render — plus content
  // loading above it — shifts the target. Detect the widget in the target, its
  // container, or following it in document order.
  if (target.classList.contains('yotpo-product-reviews-section')) return true;
  if (target.querySelector?.('.yotpo-widget-instance, .yotpo')) return true;

  const container = target.closest(
    '.shopify-section, .custom-container, .yotpo-product-reviews-section'
  );
  if (container?.querySelector('.yotpo-widget-instance, .yotpo')) return true;

  return false;
}

/**
 * Scrolls to the target after deferred content loads, then keeps the target
 * aligned while async widgets reflow. Aborts re-alignment on user scroll input.
 * @param {Element} target
 */
async function scrollToTargetWhenSettled(target) {
  await forcePendingLazySections();

  // Let injected markup mount and layout apply before measuring.
  await nextFrame();
  await nextFrame();

  const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
  window.scrollTo({ top: targetTop(target), behavior });

  let aborted = false;
  const abort = () => {
    aborted = true;
  };
  window.addEventListener('wheel', abort);
  window.addEventListener('touchmove', abort);
  window.addEventListener('keydown', abort);
  window.addEventListener('pointerdown', abort);

  const start = performance.now();
  let lastDesired = targetTop(target);

  await new Promise((resolve) => {
    const tick = () => {
      if (aborted) {
        resolve(undefined);
        return;
      }

      // Re-align only when the target itself shifts (content loaded above it),
      // not on every frame of the in-flight smooth scroll.
      const desired = targetTop(target);
      if (Math.abs(desired - lastDesired) > REALIGN_THRESHOLD_PX) {
        lastDesired = desired;
        window.scrollTo({ top: desired, behavior });
      }

      if (performance.now() - start < SETTLE_DURATION_MS) {
        window.setTimeout(tick, REALIGN_INTERVAL_MS);
      } else {
        resolve(undefined);
      }
    };

    window.setTimeout(tick, REALIGN_INTERVAL_MS);
  });

  window.removeEventListener('wheel', abort);
  window.removeEventListener('touchmove', abort);
  window.removeEventListener('keydown', abort);
  window.removeEventListener('pointerdown', abort);
}

/**
 * @param {string} href
 * @returns {Element | null}
 */
function resolveHashTarget(href) {
  if (!href || href === '#' || href.charAt(0) !== '#') return null;

  const id = decodeURIComponent(href.slice(1));
  if (!id) return null;

  return document.getElementById(id) || document.querySelector(`[name="${CSS.escape(id)}"]`);
}

/**
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const eventTarget = event.target;
  if (!(eventTarget instanceof Element)) return;

  const anchor = eventTarget.closest('a[href^="#"]');
  if (!(anchor instanceof HTMLAnchorElement)) return;

  // Let dedicated tab navigation keep its own behaviour.
  if (anchor.closest('.xtabnav')) return;

  const href = anchor.getAttribute('href') ?? '';
  const target = resolveHashTarget(href);
  if (!target || !targetNeedsSettle(target)) return;

  event.preventDefault();

  const id = decodeURIComponent(href.slice(1));
  if (id && window.history?.replaceState) {
    window.history.replaceState(null, '', `#${id}`);
  }

  void scrollToTargetWhenSettled(target);
}

function handleInitialHash() {
  const hash = window.location.hash;
  const target = resolveHashTarget(hash);
  if (!target || !targetNeedsSettle(target)) return;

  void scrollToTargetWhenSettled(target);
}

document.addEventListener('click', onClick);

if (document.readyState === 'complete') {
  handleInitialHash();
} else {
  window.addEventListener('load', handleInitialHash, { once: true });
}
